import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";

/**
 * Storefront display currency, shared so every price site can render the salon's
 * currency symbol without prop-threading.
 *
 * Design: defaults to "ILS" so Israel renders EXACTLY as before (₪) even before a
 * salon loads. Redux-backed routes get the currency for free via <ReduxCurrencySync/>;
 * the API-fetch pages (Shop / ProductDetail / Checkout / OrderDetails) push their
 * locally-loaded salon currency in through useSetSalonCurrency().
 */
const CurrencyContext = createContext(null);

const NOOP = () => {};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  return (ctx && ctx.currency) || "ILS";
};

export const useSetSalonCurrency = () => {
  const ctx = useContext(CurrencyContext);
  return (ctx && ctx.setSalonCurrency) || NOOP;
};

export const CurrencyProvider = ({ children, initialCurrency }) => {
  const [currency, setCurrencyState] = useState(initialCurrency || "ILS");

  const setSalonCurrency = useCallback((code) => {
    const c = (code == null ? "" : String(code)).trim();
    if (c) setCurrencyState(c.toUpperCase());
  }, []);

  const value = useMemo(
    () => ({ currency, setSalonCurrency }),
    [currency, setSalonCurrency]
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

/**
 * Mirrors the redux salon currency into the context. Mounted once at the app root
 * (inside the redux Provider). Routes that dispatch getSalonSpecificInfo populate
 * state.getSalonReducer.data.currency, which this picks up automatically.
 */
export const ReduxCurrencySync = () => {
  const reduxCurrency = useSelector(
    (state) => state.getSalonReducer?.data?.currency
  );
  const setSalonCurrency = useSetSalonCurrency();
  useEffect(() => {
    if (reduxCurrency) setSalonCurrency(reduxCurrency);
  }, [reduxCurrency, setSalonCurrency]);
  return null;
};

export default CurrencyContext;
